import type { SortDirection } from "@/components/ui/SortableTableHeader";

import {
  coerceReportFilterOperator,
  INSIDE_SALES_REPORT_FILTER_COLUMNS,
  INSIDE_SALES_REPORT_FILTER_OPERATORS,
  type InsideSalesReportColumnFilter,
  type InsideSalesReportFilterOperator,
  type InsideSalesSalesOrderReportSortKey,
} from "./insideSalesSalesOrderReport";

export const INSIDE_SALES_REPORT_FILTER_STORAGE_KEY_PREFIX = "hub:inside-sales:sales-order-report-filters:v1";

export type InsideSalesReportFilterSnapshot = {
  search: string;
  mine: boolean;
  columnFilters: InsideSalesReportColumnFilter[];
  sortKey: InsideSalesSalesOrderReportSortKey;
  sortDir: SortDirection;
};

const SNAPSHOT_VERSION = 1;
const COLUMN_KEYS = new Set(INSIDE_SALES_REPORT_FILTER_COLUMNS.map((column) => column.key));
const OPERATOR_KEYS = new Set(INSIDE_SALES_REPORT_FILTER_OPERATORS.map((entry) => entry.key));

export const DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT: InsideSalesReportFilterSnapshot = {
  search: "",
  mine: false,
  columnFilters: [],
  sortKey: "submitted",
  sortDir: "desc",
};

export function insideSalesReportFilterStorageKey(userId: string): string {
  return `${INSIDE_SALES_REPORT_FILTER_STORAGE_KEY_PREFIX}:${userId}`;
}

function parseColumnFilter(raw: unknown): InsideSalesReportColumnFilter | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const column = record.column;
  if (typeof column !== "string" || !COLUMN_KEYS.has(column as InsideSalesSalesOrderReportSortKey)) {
    return null;
  }
  const typedColumn = column as InsideSalesSalesOrderReportSortKey;
  const operatorRaw = record.operator;
  const operator: InsideSalesReportFilterOperator =
    typeof operatorRaw === "string" && OPERATOR_KEYS.has(operatorRaw as InsideSalesReportFilterOperator)
      ? coerceReportFilterOperator(typedColumn, operatorRaw as InsideSalesReportFilterOperator)
      : "contains";
  const id = typeof record.id === "string" && record.id.trim() !== "" ? record.id.trim() : `filter-${typedColumn}`;
  return {
    id,
    column: typedColumn,
    operator,
    value: typeof record.value === "string" ? record.value : "",
    valueTo: typeof record.valueTo === "string" ? record.valueTo : "",
  };
}

export function parseInsideSalesReportFilterSnapshot(raw: unknown): InsideSalesReportFilterSnapshot | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (record.v !== SNAPSHOT_VERSION) {
    return null;
  }
  const sortKeyRaw = record.sortKey;
  const sortKey =
    typeof sortKeyRaw === "string" && COLUMN_KEYS.has(sortKeyRaw as InsideSalesSalesOrderReportSortKey)
      ? (sortKeyRaw as InsideSalesSalesOrderReportSortKey)
      : DEFAULT_INSIDE_SALES_REPORT_FILTER_SNAPSHOT.sortKey;
  const sortDir = record.sortDir === "asc" || record.sortDir === "desc" ? record.sortDir : "desc";
  const columnFiltersRaw = Array.isArray(record.columnFilters) ? record.columnFilters : [];
  return {
    search: typeof record.search === "string" ? record.search : "",
    mine: record.mine === true,
    columnFilters: columnFiltersRaw
      .map((entry) => parseColumnFilter(entry))
      .filter((entry): entry is InsideSalesReportColumnFilter => entry != null),
    sortKey,
    sortDir,
  };
}

function browserLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function readInsideSalesReportFilterSnapshot(userId: string): InsideSalesReportFilterSnapshot | null {
  const trimmed = userId.trim();
  if (trimmed === "") {
    return null;
  }
  const storage = browserLocalStorage();
  if (storage == null) {
    return null;
  }
  try {
    const raw = storage.getItem(insideSalesReportFilterStorageKey(trimmed));
    if (raw == null || raw.trim() === "") {
      return null;
    }
    return parseInsideSalesReportFilterSnapshot(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function writeInsideSalesReportFilterSnapshot(
  userId: string,
  snapshot: InsideSalesReportFilterSnapshot,
): void {
  const trimmed = userId.trim();
  if (trimmed === "") {
    return;
  }
  const storage = browserLocalStorage();
  if (storage == null) {
    return;
  }
  try {
    storage.setItem(
      insideSalesReportFilterStorageKey(trimmed),
      JSON.stringify({ v: SNAPSHOT_VERSION, ...snapshot }),
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function clearInsideSalesReportFilterSnapshot(userId: string): void {
  const trimmed = userId.trim();
  if (trimmed === "") {
    return;
  }
  const storage = browserLocalStorage();
  if (storage == null) {
    return;
  }
  try {
    storage.removeItem(insideSalesReportFilterStorageKey(trimmed));
  } catch {
    // Ignore.
  }
}
